'use client';

import type { FragmentType } from 'src/gql';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@savvy-kit/components/button';
import { RadioGroup, RadioGroupItem } from '@savvy-kit/components/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@savvy-kit/components/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { isNil } from 'es-toolkit';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Info as InfoIcon } from 'lucide-react';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { MonetaryInputField } from 'src/components/react-hook-form-components/monetary-input-field';
import { unmaskFragment } from 'src/gql';
import { z } from 'zod';

import type { TaxEnrollmentTypeName } from '@savvy/constants/generated/gql-enums';

import { TaxEnrollmentFormFragment } from './tax-enrollment-form-fragment';
import { useTaxEnrollmentActions } from './tax-enrollment-mutations';
import { castToTaxEnrollmentType, getNotificationTimingText, isAtLeastOneClientFiling } from './tax-utils';

// Re-export for backwards compatibility
export { TaxEnrollmentFormFragment } from './tax-enrollment-form-fragment';

const taxEnrollmentSchema = z.object({
  enrollmentType: z.custom<TaxEnrollmentTypeName>(),
  billingAmount: z.string().refine((val) => {
    if (!val) return false;
    const price = parseFloat(val);
    if (price < 0 || price > 895) return false;
    return true;
  }, 'Billing amount must be between $0 and $895'),
  coClientEnrollmentType: z.custom<TaxEnrollmentTypeName>(),
  coClientBillingAmount: z.string().refine((val) => {
    if (!val) return false;
    const price = parseFloat(val);
    if (price < 0 || price > 895) return false;
    return true;
  }, 'Billing amount must be between $0 and $895'),
  isLateFiling: z.boolean(),
});

export type TaxEnrollmentValues = z.input<typeof taxEnrollmentSchema>;

type TaxEnrollmentFormProps = {
  readonly householdFrag: FragmentType<typeof TaxEnrollmentFormFragment> | undefined;
  readonly onClose: () => void;
};

export function TaxEnrollmentForm({ householdFrag, onClose }: TaxEnrollmentFormProps) {
  const household = unmaskFragment(TaxEnrollmentFormFragment, householdFrag);
  const taxEnrollments = household?.taxEnrollments ?? [];
  const hasCoClient = household?.coClient !== null;
  const { enableTaxFiling } = useFlags();

  const getDefaultValues = (): TaxEnrollmentValues => {
    // If no existing enrollments, return default values
    if (taxEnrollments.length === 0) {
      return {
        enrollmentType: hasCoClient ? 'savvy_joint' : 'savvy_single',
        billingAmount: '895',
        coClientEnrollmentType: 'savvy_single',
        coClientBillingAmount: '895',
        isLateFiling: false,
      };
    }

    // Return values to edit based on existing enrollments
    const primaryEnrollment = taxEnrollments.find((enrollment) => {
      return enrollment.billableClient.id === household?.primaryClient.id;
    });
    const coClientEnrollment = taxEnrollments.find((enrollment) => {
      return enrollment.billableClient.id === household?.coClient?.id;
    });

    return {
      enrollmentType: primaryEnrollment ? castToTaxEnrollmentType(primaryEnrollment.enrollmentType) : 'savvy_single',
      billingAmount: primaryEnrollment?.price ?? '895',
      coClientEnrollmentType: coClientEnrollment
        ? castToTaxEnrollmentType(coClientEnrollment.enrollmentType)
        : 'savvy_single',
      coClientBillingAmount: coClientEnrollment?.price ?? '895',
      isLateFiling: false,
    };
  };

  const methods = useForm<TaxEnrollmentValues>({
    resolver: zodResolver(taxEnrollmentSchema),
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  });
  const { formState } = methods;

  const { createNewEnrollments, updateExistingEnrollments } = useTaxEnrollmentActions();

  const taxServiceOptions = (showJointOption = false) => {
    return [
      { value: 'savvy_single', label: 'Single filing' },
      ...(showJointOption ? [{ value: 'savvy_joint', label: 'Joint filing' }] : []),
      { value: 'external_cpa', label: 'External CPA' },
      { value: 'transcript_services_only', label: 'Transcript services only' },
      { value: 'other', label: 'Other' },
    ];
  };

  const enrollmentType = methods.watch('enrollmentType');
  const coClientEnrollmentType = methods.watch('coClientEnrollmentType');
  const isLateFiling = methods.watch('isLateFiling');

  const jointFiling = enrollmentType === 'savvy_joint';
  const showPrimaryBilling = enrollmentType === 'savvy_single' || enrollmentType === 'savvy_joint';
  const showCoClientBilling = coClientEnrollmentType === 'savvy_single';
  const enrollingWithSavvy = isAtLeastOneClientFiling(enrollmentType, hasCoClient, coClientEnrollmentType);
  const notificationTimingText = getNotificationTimingText(Boolean(enableTaxFiling), isLateFiling);

  if (!household) {
    return null;
  }

  const handleSubmit = async (data: TaxEnrollmentValues) => {
    const isEditMode = !isNil(household.taxEnrollments) && household.taxEnrollments.length > 0;
    if (isEditMode) {
      await updateExistingEnrollments(data, household);
    } else {
      await createNewEnrollments(data, household);
    }
    onClose();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <div className="flex flex-col gap-6 px-8 pb-8">
          <div className="flex flex-col gap-3">
            <h2 className=" font-semibold">{jointFiling ? household.name : household.primaryClient.user.fullName}</h2>
            <EnrollmentFieldsRow
              enrollmentTypeFieldName="enrollmentType"
              billingAmountFieldName="billingAmount"
              showBilling={showPrimaryBilling}
              showJointOption={hasCoClient}
              taxServiceOptions={taxServiceOptions}
            />
          </div>
          {hasCoClient && !jointFiling && (
            <div className="flex flex-col gap-3">
              <h2 className="font-semibold">{household.coClient?.user.fullName}</h2>
              <EnrollmentFieldsRow
                enrollmentTypeFieldName="coClientEnrollmentType"
                billingAmountFieldName="coClientBillingAmount"
                showBilling={showCoClientBilling}
                showJointOption={false}
                taxServiceOptions={taxServiceOptions}
              />
            </div>
          )}
          {enrollingWithSavvy && (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium text-dark">Is this a late filing?</p>
              <Controller
                name="isLateFiling"
                control={methods.control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value ? 'true' : 'false'}
                    onValueChange={(val) => field.onChange(val === 'true')}
                    className="flex flex-row gap-6"
                  >
                    <RadioGroupItem id="is-late-filing-no" value="false" label="No" />
                    <RadioGroupItem
                      id="is-late-filing-yes"
                      value="true"
                      label="Yes, I will file after the deadline (extension)"
                    />
                  </RadioGroup>
                )}
              />
              <p className="text-sm text-dark">{notificationTimingText}</p>
            </div>
          )}
          <div className="flex flex-row justify-end w-full gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={!formState.isValid}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

type EnrollmentFieldsRowProps = {
  readonly enrollmentTypeFieldName: 'enrollmentType' | 'coClientEnrollmentType';
  readonly billingAmountFieldName: 'billingAmount' | 'coClientBillingAmount';
  readonly showBilling: boolean;
  readonly showJointOption: boolean;
  readonly taxServiceOptions: (showJointOption?: boolean) => { value: string; label: string }[];
};

function EnrollmentFieldsRow({
  enrollmentTypeFieldName,
  billingAmountFieldName,
  showBilling,
  showJointOption,
  taxServiceOptions,
}: EnrollmentFieldsRowProps) {
  const { control } = useFormContext<TaxEnrollmentValues>();
  const options = taxServiceOptions(showJointOption);
  return (
    <div className="flex gap-7">
      <div className="flex flex-col gap-2 w-[calc(50%-16px)]">
        <p className="text-sm font-medium text-dark">Filing type</p>
        <Controller
          name={enrollmentTypeFieldName}
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              items={options.map((option) => ({ value: option.value, label: option.label }))}
              onValueChange={field.onChange}
            >
              <SelectTrigger className="w-full" id={`${enrollmentTypeFieldName}-trigger`}>
                <SelectValue placeholder="Select filing type" />
              </SelectTrigger>
              <SelectContent className="z-[1500]">
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>
      {showBilling && <BillingAmountField fieldName={billingAmountFieldName} />}
    </div>
  );
}

type BillingAmountFieldProps = {
  readonly fieldName: 'billingAmount' | 'coClientBillingAmount';
};

function BillingAmountField({ fieldName }: BillingAmountFieldProps) {
  return (
    <div className="flex flex-col gap-2 flex-1">
      <div className="flex flex-row gap-1 items-center">
        <label htmlFor={fieldName} className="text-sm font-medium text-dark">
          Billing amount
        </label>
        <Tooltip>
          <TooltipTrigger render={<InfoIcon className="size-[18px]" />} />
          <TooltipContent side="top" className="z-[1500] max-w-[240px]">
            This will be deducted from the client's taxable brokerage account alongside their advisory billing. You may
            adjust or subsidize the cost at your discretion.
          </TooltipContent>
        </Tooltip>
      </div>
      <MonetaryInputField name={fieldName} allowDecimals inputClassName="text-base" />
    </div>
  );
}
